import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

/** How long a toast stays before it dismisses itself. */
const DISMISS_AFTER_MS = 4000;

type Tone = 'success' | 'danger';

type Toast = { id: number; tone: Tone; message: string };

/**
 * Transient confirmation of something that already happened.
 *
 * Hand-written rather than `sonner`, for the same reason `modal.tsx` is not
 * Radix: the whole behaviour is one small file, and a dependency whose job is a
 * fixed-position list with a timer is a dependency that costs more to keep
 * current than to own.
 *
 * **This is not an error channel.** Every form here already routes server
 * validation to the field that caused it (`entity-form.tsx`) and shows anything
 * form-level in a `role="alert"` beside the controls, where the user is looking.
 * A toast is for the opposite case — the write succeeded and the thing that
 * proved it is about to close, so there is nowhere left to say so. The danger
 * tone exists for a failure whose dialog has *also* gone (a delete), never as a
 * replacement for the inline message.
 *
 * Screen-reader announcement is the reason the two tones render into two
 * regions rather than one. `aria-live="polite"` waits for a pause; a failed
 * write should not. Assertive is reserved for the danger tone and nothing else,
 * because a queue of assertive announcements interrupts whatever the user is
 * doing, repeatedly.
 */
const ToastContext = createContext<{
  success: (message: string) => void;
  error: (message: string) => void;
} | null>(null);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // A counter rather than Date.now(): two toasts raised in the same millisecond
  // — a refresh that fails for two queries at once — would collide on key.
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback((tone: Tone, message: string) => {
    const id = (nextId.current += 1);

    setToasts((current) => [...current, { id, tone, message }]);
  }, []);

  // Identity-stable, so a component can depend on it in an effect or a mutation
  // callback without re-subscribing on every render.
  const value = useMemo(
    () => ({
      success: (message: string) => push('success', message),
      error: (message: string) => push('danger', message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

/**
 * Raise a toast.
 *
 * Throws outside a provider rather than returning a no-op: a confirmation that
 * silently does not appear is the failure this component exists to prevent.
 */
export const useToast = (): { success: (message: string) => void; error: (message: string) => void } => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used inside a ToastProvider');
  }

  return context;
};

/**
 * The two live regions and the stack they render.
 *
 * Both regions exist from first paint, empty. A live region inserted into the
 * document *already containing* its message is not announced by most screen
 * readers — the region has to be there for the insertion to be observed — which
 * is why this is not conditional on `toasts.length`.
 *
 * `z-[60]` puts it above the modal overlay at `z-50`: a toast raised by a dialog
 * closing must not appear behind the dialog it outlived.
 */
const ToastViewport = ({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: number) => void;
}) => (
  <div className="pointer-events-none fixed top-4 right-4 z-[60] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2">
    <div aria-live="polite" aria-atomic="false" className="contents">
      {toasts
        .filter((toast) => toast.tone === 'success')
        .map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
    </div>
    <div aria-live="assertive" aria-atomic="false" className="contents">
      {toasts
        .filter((toast) => toast.tone === 'danger')
        .map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
    </div>
  </div>
);

const ToastItem = ({ toast, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) => {
  const { t } = useTranslation();

  // Keyed on the id, so the timer belongs to this toast and re-running the
  // effect for a different one cannot dismiss it early.
  useEffect(() => {
    const timer = window.setTimeout(() => onDismiss(toast.id), DISMISS_AFTER_MS);

    return () => window.clearTimeout(timer);
  }, [toast.id, onDismiss]);

  return (
    <div
      data-testid="toast"
      data-tone={toast.tone}
      className={cn(
        // pointer-events-auto: the wrapper disables them so a toast cannot
        // swallow a click meant for the page under it, but the close button
        // still has to be clickable.
        'pointer-events-auto flex items-start gap-2 rounded-lg border px-3 py-2 shadow-lg',
        toast.tone === 'success'
          ? 'border-border-default bg-success-bg text-success-fg'
          : 'border-border-default bg-danger-bg text-danger-fg',
      )}
    >
      <p className="min-w-0 flex-1 text-xs break-words">{toast.message}</p>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label={t('common.dismiss')}
        className="shrink-0 rounded focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
      >
        <X aria-hidden className="size-3.5" />
      </button>
    </div>
  );
};
